import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { Button, Card, CardBody, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap';
import { FilterSelect, ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, filterHelpers, UserContext } from '../utils';
import _ from "lodash";

// A save-with-warnings toast stays up long enough to read the sentences before it closes.
const PM_CHECK_WARNING_TOAST_MS = 15000;

export default function PMCheck() {
   const { currentUser } = useContext(UserContext);
   const [allow, setAllowed] = useState(false);
   const [barcode, setBarcode] = useState(null);
   const [getDia, setGetDia] = useState(false);
   const [getItem, setGetItem] = useState(true);   // show item selection (true) or show data entry (false) when isNew is false
   const [getTail, setGetTail] = useState(false);
   const [getTraverse, setGetTrav] = useState(false);
   const [getTube, setGetTube] = useState(false);
   const [getWgt, setGetWgt] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [maxOD, setMaxOD] = useState(null);
   const [maxWgt, setMaxWgt] = useState(null);
   const [minOD, setMinOD] = useState(null);
   const [minWgt, setMinWgt] = useState(null);
   const [model, setModel] = useState({});
   const [open, setOpen] = useState(null);
   const [opts, setOpts] = useState([]);           // for item drop-down
   const [ship, setShip] = useState('P');
   const [showDia, setShowDia] = useState(false);
   const [showSave, setShowSave] = useState(false);
   const [showTail, setShowTail] = useState(false);
   const [showTraverse, setShowTrav] = useState(false);
   const [showTube, setShowTube] = useState(false);
   const [showWgt, setShowWgt] = useState(false);
   const [spin, setSpinner] = useState(false);
   const [srch, setSrch] = useState('P');
   const [tail, setTail] = useState(null);
   const [traverse, setTraverse] = useState(null);
   const [tube, setTube] = useState('');
   const [yarnItems, setYarnItems] = useState([]);
   const navigate = useNavigate();
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   let modalClassName = (currentUser && currentUser.theme) ? `${currentUser.theme}` : "lightTheme";

   const SHIP_MAIN = { value: 'P', label: "Pallet(s)" };
   const shipOpts = [
      { value: 'B', label: "Box(es)" },
      { value: 'P', label: "Pallet(s)" }
   ];
   const SRCH_MAIN = { value: 'P', label: "PO Number" };
   const searchOpts = [
      { value: 'P', label: "PO Number" },
      { value: 'C', label: "Container Number" }
   ];
   const NO_ITEM = { value: "na", label: "--- Not available ---", hiTi: false, onHold: false };
   const [filters, setFilters] = useState([
      { filterName: "searchType", value: SRCH_MAIN },
      { filterName: "shipType", value: SHIP_MAIN },
      { filterName: "yarnItem", value: NO_ITEM }
   ]);


   useEffect(() => {
      document.title = `PM Check`
      async function init() {
         await sleep(50);  //allows context to be loaded
         if (!currentUser || (!currentUser.isAdmin && !currentUser.isPowerUser && !currentUser.isWarehouseEdit)) {
            toast.error("You are not authorized to view this page");
            return;
         }

         setAllowed(true);
         inputFocus("searchInput");
      }
      init();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      async function setItems() {
         await sleep(50);  // allows time for input to be recognized
         if (getItem) {
            const itemFilter = (opts) ? opts[0] : NO_ITEM;

            let newFilters = filterHelpers.getFilters(filters, { filterName: "shipType", value: SHIP_MAIN });
            newFilters = filterHelpers.getFilters(newFilters, { filterName: "searchType", value: SRCH_MAIN });
            newFilters = filterHelpers.getFilters(newFilters, { filterName: "yarnItem", value: itemFilter });
            setFilters(newFilters);
         }
      }
      setItems();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [opts])

   function completeForm() {
      setSpinner(true);
      api
         .post(`Mobile/SavePurchaseCheck`, model)
         .then(response => {
            if (response.data.success) {
               // A saved check can carry warnings (the email, PDF or attachment failed); they need a person to act.
               if (response.data.warnings?.length) {
                  toast.warning(response.data.warnings.join(" "), { autoClose: PM_CHECK_WARNING_TOAST_MS });
               } else {
                  toast.success("PM Check saved successfully");
               }
               navigate(constants.ENDPOINTS.mobileHome);
            } else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSpinner(false);
         });
   }

   function getObjectByKey(array, key, value) {
      for (var i = 0; i < array.length; i++) {
         if (array[i][key] === value) {
            return array[i];
         }
      }
      return null;
   }

   function getYarnItems() {
      if (!loaded && barcode) {
         setSpinner(true);
         var url = (srch === 'P') ? `Mobile/GetYarnItemsByPO/${barcode}` : `Mobile/GetYarnItemsByContainer/${barcode}`;

         api
            .post(url)
            .then(response => {
               if (response.data.success) {
                  setModel(response.data.model);          // PurchaseItemCheck object
                  setYarnItems(response.data.yarnItems);  // List<PurchasedMaterialYarnItem>
                  setOpts(response.data.opts);            // List<YarnItemOption> for item drop-down
                  setGetItem(true);
                  setLoaded(true);
               }
               else {
                  toast.error(response.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setBarcode(null);
               setSpinner(false);
            });
      }
   }

   function handleChange() {
      // Clicked the lookup button
      getYarnItems();
   }

   const handleFocus = (event) => event.target.select();

   const handleKeypress = e => {
      // On key press for search input field
      if (e.charCode === 13 || e.key === 'Enter') {
         getYarnItems();
      }
   };

   async function inputFocus(fieldname) {
      await sleep(50);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   function lookup(barcode) {
      // On change of search input field
      setBarcode(barcode);
      inputFocus("searchInput");
   }

   function onChange(field, value) {
      let changes = Object.assign({}, model);
      changes[field] = value;
      setModel(changes);
   }

   function onItemChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      const itm = _.find(opts, (m) => m.value === newFilterValue.value);
      let changes = Object.assign({}, model);
      changes.itemNumber = newFilterValue.value;
      changes.itemWithDesc = newFilterValue.label;
      changes.onHold = itm.onHold || false;
      changes.hiTi = itm.hiTi || false;
      setModel(changes);
   }

   function onShipChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);
      setShip(newFilterValue.value);

      let changes = Object.assign({}, model);
      changes.shipmentType = newFilterValue.value;
      changes.shipmentTypeText = newFilterValue.value === 'B' ? 'No. of Boxes' : 'No. of Pallets';
      setModel(changes);
   }

   function onSrchChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);
      setSrch(newFilterValue.value);
      inputFocus("searchInput");
   }

   function processDia() {
      if (model.pkgDiameter >= minOD && model.pkgDiameter <= maxOD) {
         model.pkgDiameterInSpec = true;
      }
      setGetDia(false);
      setShowDia(true);
      setGetTail(true);
      inputFocus("tail");
   }

   function processItem() {
      if (!model.packingSlipMatches) {
         toast.error("STOP! Contact Purchasing Dept.");
         return;
      }

      if (!model.itemNumber || model.itemNumber.startsWith('na')) {
         toast.error("A valid item is required");
         return;
      }

      if (!model.qty || model.qty < 1) {
         toast.error("Quantity must be > 0");
         return;
      }

      let itm = getObjectByKey(yarnItems, 'itemNumber', model.itemNumber);
      if (!itm) {
         toast.error("Error finding item in list of items");
         return;
      }

      model.container = itm.container;
      model.itemWithDesc = itm.itemWithDesc;
      model.poNumber = itm.poNumber;
      model.specNum = itm.specNum;
      setMaxOD(itm.maxOD);
      setMinOD(itm.minOD);
      setMaxWgt(itm.maxWeight);
      setMinWgt(itm.minWeight);
      setTail(itm.transferTail);
      setTraverse(itm.traverse);
      setTube(itm.tubeColorSize);

      setGetItem(false);
      setGetWgt(true);
      inputFocus("wgt");
   }

   function processTail() {
      if (Number(model.transferTail) === tail) {
         model.transferTailInSpec = true;
      }
      setGetTail(false);
      setShowTail(true);
      setGetTube(true);
      inputFocus("tube");
   }

   function processTraverse() {
      if (Number(model.pkgTraverse) === traverse) {
         model.pkgTraverseInSpec = true;
      }
      setGetTrav(false);
      setShowTrav(true);
      setGetDia(true);
      inputFocus("diameter");
   }

   function processTube() {
      if (!model.tubeColorSizeInSpec && !model.tubeDiscrepancy) {
         toast.error("Tube Discrepancy is required");
         return;
      }
      setGetTube(false);
      setShowTube(true);
      setShowSave(true);
   }

   function processWgt() {
      if (model.pkgWeight >= minWgt && model.pkgWeight <= maxWgt) {
         model.pkgWeightInSpec = true;
      }
      setGetWgt(false);
      setShowWgt(true);
      setGetTrav(true);
      inputFocus("trv");
   }

   function reset() {
      setBarcode(null);
      setGetDia(false);
      setGetItem(true);
      setGetTail(false);
      setGetTrav(false);
      setGetTube(false);
      setGetWgt(false);
      setLoaded(false);
      setMaxOD(null);
      setMinOD(null);
      setMaxWgt(null);
      setMinWgt(null);
      setModel({});
      setOpen(null);
      setOpts([]);  // items on PO or in container
      setShip('P');
      setShowDia(false);
      setShowSave(false);
      setShowTail(false);
      setShowTrav(false);
      setShowTube(false);
      setShowWgt(false);
      setSpinner(false);
      setSrch('P');
      setTail(null);
      setTraverse(null);
      setTube('');
      setYarnItems([]);
      inputFocus("searchInput");
   }

   function togglePriority() {
      let changes = Object.assign({}, model);
      changes["isPriority"] = !model.isPriority;
      setModel(changes);
   }

   function toggleSlip() {
      let changes = Object.assign({}, model);
      changes["packingSlipMatches"] = !model.packingSlipMatches;
      setModel(changes);
   }

   function toggleTube() {
      let changes = Object.assign({}, model);
      changes["tubeColorSizeInSpec"] = !model.tubeColorSizeInSpec;
      setModel(changes);
   }

   return (
      <>
         <DesktopContainer screenName="Receiving PM Check">
            <ServiceThreadSpinner on={spin}>
               {allow ?
                  <div>
                     {/*form is initialized, but a PO or Container must be provided to continue*/}
                     {!loaded ?
                        <div>
                           <Row>
                              <Col className="h5 fw-bold" xs={12}>
                                 Find By:
                              </Col>
                           </Row>
                           <Row>
                              <Col xs={12}>
                                 <FilterSelect
                                    filterName="searchType"
                                    options={searchOpts}
                                    value={filterHelpers.getValue(filters, "searchType")}
                                    onChangeCallback={e => onSrchChange(e)}
                                 />
                              </Col>
                           </Row>
                           <br />
                           <div>
                              <Row className="justify-content-center">
                                 <Col xs="12">
                                    <Button
                                       title="manual entry"
                                       className="shredButton w-100 btn-md"
                                       onClick={() => handleChange()}>
                                       Lookup
                                    </Button>
                                 </Col>
                              </Row>
                              <Row className="lineheight8">&nbsp;</Row>
                              <Row className="justify-content-center">
                                 <Col xs="12">
                                    <Input
                                       name="searchInput"
                                       placeholder="Enter PO or Container #"
                                       className="h-100"
                                       onChange={(e) => lookup(e.target.value)}
                                       onKeyPress={handleKeypress}
                                    />
                                 </Col>
                              </Row>
                           </div>
                        </div>
                        : null
                     }

                     {/*PO or container provided, now select item from available options*/}
                     {loaded && getItem ?
                        <div>
                           <Card className="mobileBlueBgAlt">
                              <CardBody className="mobileBlueBgAlt">
                                 <Table borderless size="sm">
                                    <tbody>
                                       <tr>
                                          <td colSpan="2" className="data-cell my-0 py-0">
                                             <label className="text-black h6">
                                                <input type="checkbox"
                                                   checked={model.packingSlipMatches}
                                                   className="me-2"
                                                   onChange={(e) => toggleSlip(e.target.checked)}
                                                />
                                                Packing Slip Matches Received Material?
                                             </label>
                                          </td>
                                       </tr>
                                       <tr>
                                          <td colSpan="2" className="data-cell my-0 py-0">
                                             <label className="text-black h6">
                                                <input type="checkbox"
                                                   checked={model.isPriority}
                                                   className="me-2"
                                                   onChange={(e) => togglePriority(e.target.checked)}
                                                />
                                                Requires Priority Rush?
                                             </label>
                                          </td>
                                       </tr>
                                       <tr>
                                          <td className="data-cell my-0 py-0" width="100px">
                                             <label className="text-black h6 fw-bold my-0">Item:</label>
                                          </td>
                                          <td className="data-cell my-0 py-0">
                                             <FilterSelect
                                                className="text-black"
                                                filterName="yarnItem"
                                                options={opts}
                                                value={filterHelpers.getValue(filters, "yarnItem")}
                                                onChangeCallback={e => onItemChange(e)}
                                             />
                                          </td>
                                       </tr>
                                       <tr>
                                          <td className="data-cell my-0 py-0">
                                             <label className="text-black h6 fw-bold my-0">Quantity:</label>
                                          </td>
                                          <td className="data-cell my-0 py-0">
                                             <Input
                                                type="number"
                                                className="h-100 py-1"
                                                value={model.qty}
                                                required
                                                onChange={(e) => { onChange("qty", e.target.value) }}
                                             />
                                          </td>
                                       </tr>
                                       <tr>
                                          <td className="data-cell my-0 py-0">
                                             <label className="text-black h6 fw-bold my-0">Shipped In:</label>
                                          </td>
                                          <td className="data-cell my-0 py-0">
                                             <FilterSelect
                                                filterName="shipType"
                                                options={shipOpts}
                                                value={filterHelpers.getValue(filters, "shipType")}
                                                onChangeCallback={e => onShipChange(e)}
                                             />
                                          </td>
                                       </tr>
                                       <tr><td colSpan="2">&nbsp;</td></tr>
                                       <tr>
                                          <td colSpan="2" className="data-cell my-0 py-0">
                                             <Button
                                                title="check item"
                                                className="shredButton w-100 btn-md"
                                                onClick={() => processItem()}>
                                                Continue
                                             </Button>
                                          </td>
                                       </tr>
                                       <tr>
                                          <td colSpan="2" className="data-cell my-0 py-0">
                                             <Button
                                                title="reset"
                                                className="btn w-100 btn-md my-3"
                                                onClick={() => reset()}>
                                                Cancel
                                             </Button>
                                          </td>
                                       </tr>
                                    </tbody>
                                 </Table>
                              </CardBody>
                           </Card>
                        </div>
                        : null
                     }

                     {/*item was selected, now get data on it*/}
                     {loaded && !getItem ?
                        <div>
                           <Card className="mobileBlueBgAlt">
                              <CardBody className="mobileBlueBgAlt">
                                 <Table borderless size="sm">
                                    <tbody>
                                       <tr>
                                          <td className="data-cell my-0 py-0" width="85px">
                                             <label className="text-black h6 fw-bold my-0">Item:</label>
                                          </td>
                                          <td className="data-cell my-0 py-0">
                                             <label className="text-white h6">{model.itemNumber}</label>
                                          </td>
                                       </tr>
                                       <tr>
                                          <td className="data-cell my-0 py-0">
                                             <label className="text-black h6 fw-bold my-0">PO #:</label>
                                          </td>
                                          <td className="data-cell my-0 py-0">
                                             <label className="text-white h6">{model.poNumber}</label>
                                          </td>
                                       </tr>
                                       <tr>
                                          <td className="data-cell my-0 py-0">
                                             <label className="text-black h6 fw-bold my-0">Container:</label>
                                          </td>
                                          <td className="data-cell my-0 py-0">
                                             <label className="text-white h6">{model.container}</label>
                                          </td>
                                       </tr>
                                       {model.onHold === true ? (
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-black h6 fw-bold my-0">QC Hold:</label>
                                             </td>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-white h6">Yes</label>
                                             </td>
                                          </tr>
                                       ): null}
                                       {model.hiTi === true ? (
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-black h6 fw-bold my-0">Status:</label>
                                             </td>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-white h6">HI or TI</label>
                                             </td>
                                          </tr>
                                       ): null}
                                       <tr>
                                          <td className="data-cell my-0 py-0">
                                             <label className="text-black h6 fw-bold my-0">{ship === 'B' ? "Box(es):" : "Pallet(s):"}</label>
                                          </td>
                                          <td className="data-cell my-0 py-0">
                                             <label className="text-white h6">{model.qty}</label>
                                          </td>
                                       </tr>
                                       <tr>
                                          <td colSpan="2" className="data-cell my-0 py-0">
                                             <label className="text-black h6"><input type="checkbox" readOnly checked={model.packingSlipMatches} className="me-2" />Packing Slip Matches Received Material?</label>
                                          </td>
                                       </tr>
                                       <tr>
                                          <td colSpan="2" className="data-cell my-0 py-0">
                                             <label className="text-black h6"><input type="checkbox" readOnly checked={model.isPriority} className="me-2" />Requires Priority Rush?</label>
                                          </td>
                                       </tr>

                                       {showWgt ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">
                                                <label className="text-black h6"><input type="checkbox" readOnly checked={model.pkgWeightInSpec} className="me-2" />Package Weight in Spec?</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {showWgt && !model.pkgWeightInSpec ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">&nbsp;&nbsp;&nbsp;&nbsp;
                                                <label className="text-black h6 fw-bold my-0">Out of Spec Weight: {`${model.pkgWeight}`}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }

                                       {showTraverse ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">
                                                <label className="text-black h6"><input type="checkbox" readOnly checked={model.pkgTraverseInSpec} className="me-2" />Package Traverse in Spec?</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {showTraverse && !model.pkgTraverseInSpec ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">&nbsp;&nbsp;&nbsp;&nbsp;
                                                <label className="text-black h6 fw-bold my-0">Out of Spec Traverse: {`${model.pkgTraverse}`}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }

                                       {showDia ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">
                                                <label className="text-black h6"><input type="checkbox" readOnly checked={model.pkgDiameterInSpec} className="me-2" />Package Outer Diameter in Spec?</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {showDia && !model.pkgDiameterInSpec ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">&nbsp;&nbsp;&nbsp;&nbsp;
                                                <label className="text-black h6 fw-bold my-0">Out of Spec OD: {`${model.pkgDiameter}`}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }

                                       {showTail ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">
                                                <label className="text-black h6"><input type="checkbox" readOnly checked={model.transferTailInSpec} className="me-2" />Transfer Tail in Spec?</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {showTail && !model.transferTailInSpec ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">&nbsp;&nbsp;&nbsp;&nbsp;
                                                <label className="text-black h6 fw-bold my-0">Out of Spec Tail: {`${model.transferTail}`}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }

                                       {showTube ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">
                                                <label className="text-black h6"><input type="checkbox" readOnly checked={model.tubeColorSizeInSpec} className="me-2" />Tube Color/Size in Spec?</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {showTube && !model.tubeColorSizeInSpec ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">&nbsp;&nbsp;&nbsp;&nbsp;
                                                <label className="text-black h6 fw-bold my-0">Tube Discrepancy: {`${model.tubeDiscrepancy}`}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                    </tbody>
                                 </Table>

                                 <br />
                                 <Table borderless size="sm">
                                    <tbody>
                                       {getWgt ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">
                                                <label className="text-white h6 fw-bold my-0">Expected Weight: {maxWgt ? `${minWgt.toFixed(2)} - ${maxWgt.toFixed(2)}` : null}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getWgt ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-black h6 fw-bold my-0">Enter Pkg Weight:</label>
                                                <Input
                                                   name="wgt"
                                                   type="number"
                                                   className="h-100 py-1"
                                                   value={model.pkgWeight}
                                                   required
                                                   onFocus={handleFocus}
                                                   onChange={(e) => { onChange("pkgWeight", e.target.value) }}
                                                />
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getWgt ?
                                          <tr>
                                             <td className="data-cell my-0">
                                                <Button
                                                   title="validate weight"
                                                   className="shredButton w-100 btn-sm"
                                                   onClick={() => processWgt()}>
                                                   CONTINUE
                                                </Button>
                                             </td>
                                          </tr>
                                          : null
                                       }

                                       {getTraverse ?
                                          <tr>
                                             <td colSpan="2" className="data-cell my-0 py-0">
                                                <label className="text-white h6 fw-bold my-0">Expected Traverse: {traverse ? traverse.toFixed(2) : null}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getTraverse ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-black h6 fw-bold my-0">Enter Traverse:</label>
                                                <Input
                                                   name="trv"
                                                   type="number"
                                                   className="h-100 py-1"
                                                   value={model.pkgTraverse}
                                                   required
                                                   onFocus={handleFocus}
                                                   onChange={(e) => { onChange("pkgTraverse", e.target.value) }}
                                                />
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getTraverse ?
                                          <tr>
                                             <td className="data-cell my-0">
                                                <Button
                                                   title="validate trv"
                                                   className="shredButton w-100 btn-sm"
                                                   onClick={() => processTraverse()}>
                                                   CONTINUE
                                                </Button>
                                             </td>
                                          </tr>
                                          : null
                                       }

                                       {getDia ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-white h6 fw-bold my-0">Expected Diameter: {maxOD ? `${minOD.toFixed(2)} - ${maxOD.toFixed(2)}` : null}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getDia ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-black h6 fw-bold my-0">Enter Outer Diameter:</label>
                                                <Input
                                                   name="diameter"
                                                   type="number"
                                                   className="h-100 py-1"
                                                   value={model.pkgDiameter}
                                                   required
                                                   onFocus={handleFocus}
                                                   onChange={(e) => { onChange("pkgDiameter", e.target.value) }}
                                                />
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getDia ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <Button
                                                   title="validate OD"
                                                   className="shredButton w-100 btn-sm"
                                                   onClick={() => processDia()}>
                                                   CONTINUE
                                                </Button>
                                             </td>
                                          </tr>
                                          : null
                                       }

                                       {getTail ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-white h6 fw-bold my-0">Expected Tail: {tail ? tail.toFixed(2) : null}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getTail ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-black h6 fw-bold my-0">Enter Transfer Tail:</label>
                                                <Input
                                                   name="tail"
                                                   type="number"
                                                   className="h-100 py-1"
                                                   value={model.transferTail}
                                                   required
                                                   onFocus={handleFocus}
                                                   onChange={(e) => { onChange("transferTail", e.target.value) }}
                                                />
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getTail ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <Button
                                                   title="validate tail"
                                                   className="shredButton w-100 btn-sm"
                                                   onClick={() => processTail()}>
                                                   CONTINUE
                                                </Button>
                                             </td>
                                          </tr>
                                          : null
                                       }

                                       {getTube ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-white h6 fw-bold my-0">Expected Tube: {tube ? tube : null}</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getTube ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-black h6"><input type="checkbox" checked={model.tubeColorSizeInSpec} className="me-2" onChange={() => toggleTube()} />Tube Color/Size in Spec?</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getTube ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <label className="text-black h6 fw-bold my-0">If not, Enter Tube Discrepancy:</label>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getTube ?
                                          <tr>
                                             <td className="data-cell my-0 py-0">
                                                <Input
                                                   name="tube"
                                                   type="text"
                                                   maxLength={100}
                                                   className="h-100 py-1"
                                                   value={model.tubeDiscrepancy}
                                                   onFocus={handleFocus}
                                                   onChange={(e) => { onChange("tubeDiscrepancy", e.target.value) }}
                                                />
                                             </td>
                                          </tr>
                                          : null
                                       }
                                       {getTube ?
                                          <tr>
                                             <td className="data-cell my-0">
                                                <Button
                                                   title="validate tube"
                                                   className="shredButton w-100 btn-sm"
                                                   onClick={() => processTube()}>
                                                   CONTINUE
                                                </Button>
                                             </td>
                                          </tr>
                                          : null
                                       }
                                    </tbody>
                                 </Table>

                                 {showSave ?
                                    <Row className="justify-content-center">
                                       <Col xs="12">
                                          <Button
                                             title="save"
                                             className="shredButton w-100 btn-md"
                                             onClick={() => completeForm()}>
                                             SAVE
                                          </Button>
                                       </Col>
                                    </Row>
                                    : null
                                 }
                                 <Row className="justify-content-center">
                                    <Col xs="12">
                                       <Button
                                          title="reset"
                                          className="btn w-100 btn-md my-3"
                                          onClick={() => reset()}>
                                          CANCEL
                                       </Button>
                                    </Col>
                                 </Row>
                              </CardBody>
                           </Card>
                        </div>
                        : null
                     }

                  </div>
                  : null
               }


               <Modal isOpen={open !== null} contentClassName={modalClassName}>
                  <ModalHeader children={(srch === 'P') ? "Enter PO #" : "Enter Container #"} />
                  <ModalBody
                     children={
                        <BarcodeScannerComponent
                           height={350}
                           width={350}
                           onUpdate={(err, result) => {
                              if (result) {
                                 setBarcode(result.text.toUpperCase());
                                 setOpen(null);
                                 getYarnItems();
                              }
                              else {
                                 setBarcode(null);
                              }
                           }}
                           facingMode={"environment"}
                        />
                     }
                  />
                  <ModalFooter>
                     <Button
                        className="shredButton ms-7"
                        onClick={() => {
                           setOpen(null);
                           inputFocus("searchInput");
                        }
                        }>Cancel
                     </Button>
                  </ModalFooter>
               </Modal>

            </ServiceThreadSpinner>
         </DesktopContainer >
      </>
   );
}
